/**
 * @file Aura FX du chat chef : particules (sprites face camera en InstancedMesh, couleurs d'instance) animees analytiquement selon la forme
 *       (glow, ring, orbit, rise, spiral, burst), halo, anneaux lumineux au sol et pilier de lumiere (translucides en melange normal : les
 *       decors sont clairs, un melange additif y disparaitrait). Cout borne (<= 60 particules), aucune allocation par frame.
 */
import {
  CanvasTexture,
  Color,
  CylinderGeometry,
  DoubleSide,
  DynamicDrawUsage,
  FrontSide,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  RepeatWrapping,
  RingGeometry,
  Vector3,
} from 'three';
import type { BufferGeometry, Scene } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { AuraParticle, AuraStyle } from '@config/types';
import { buildSprite } from '../ammo/AmmoShapes';
import type { SpriteShape } from '../ammo/AmmoShapes';
import { colorOf, rainbowInto } from '../ammo/colors';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import { BoltRibbons } from './BoltRibbons';
import type { ToonKit } from '../toon/toon';

const TAU = Math.PI * 2;
/** Borne dure de particules actives (toutes couches confondues) ; moitie en mode eco. */
const MAX_PARTICLES = 60;
/** Nombre de particules par niveau de densite 1..5 (le niveau 5 plafonne a MAX_PARTICLES - marge). */
const COUNT_BY_DENSITY: readonly number[] = [8, 14, 24, 38, 56];
/** Duree des fondus d'apparition / disparition (s). */
const FADE_SECONDS = 0.35;
/** Au-dela de cette densite (hors eco) un tiers des particules sont des scintillements blancs. */
const SPARKLE_MIN_DENSITY = 4;
/** Centre du corps du chat (hauteur au-dessus du sol) pour les orbites. */
const BODY_Y = 0.95;

const SHAPE_OF: Record<AuraParticle, SpriteShape> = {
  dot: 'dot',
  star: 'star',
  heart: 'heart',
  flame: 'flame',
  snow: 'flake',
  petal: 'petal',
  note: 'note',
  bubble: 'bubble',
  bolt: 'bolt',
};
/** Taille de reference du sprite (rayon, unites monde) par type de particule. */
const SIZE_OF: Record<AuraParticle, number> = {
  dot: 0.17,
  star: 0.26,
  heart: 0.26,
  flame: 0.3,
  snow: 0.22,
  petal: 0.24,
  note: 0.29,
  bubble: 0.22,
  bolt: 0.34,
};

interface SpriteLayer {
  mesh: InstancedMesh;
  colors: InstancedBufferAttribute;
  used: number;
}

/** Pseudo-hasard deterministe dans [0, 1) (graines des particules : meme aura = meme figure). */
const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

export class AuraRenderer {
  private eco = false;
  private style: AuraStyle | null = null;
  private key = '';
  private fade = 0;
  private count = 0;
  private sparkEvery = 0;
  /** Sprite des particules compagnes (1 emplacement sur 3) : scintillement, ou braises pour Brasier. */
  private sparkShape: SpriteShape = 'sparkle';
  private ribbons: BoltRibbons | null = null;
  private flash = 0;
  private readonly camRight = new Vector3();
  private readonly camUp = new Vector3();
  private ready = false;

  // graines fixes par emplacement (aucun tirage en cours de jeu)
  private readonly seedA = new Float32Array(MAX_PARTICLES);
  private readonly seedB = new Float32Array(MAX_PARTICLES);
  private readonly seedC = new Float32Array(MAX_PARTICLES);

  private readonly layers = new Map<SpriteShape, SpriteLayer>();
  private spriteMat: MeshBasicMaterial | null = null;

  private halo: Mesh | null = null;
  private disc: Mesh | null = null;
  private ring: Mesh | null = null;
  private dashes: Mesh | null = null;
  private ripple: Mesh | null = null;
  private pillar: Mesh | null = null;

  private readonly colA = new Color();
  private readonly colB = new Color();
  private readonly colTmp = new Color();
  private readonly m = new Matrix4();
  private readonly q = new Quaternion();
  private readonly qRoll = new Quaternion();
  private readonly p = new Vector3();
  private readonly s = new Vector3();
  private readonly axisZ = new Vector3(0, 0, 1);
  // resultat de place()
  private ox = 0;
  private oy = 0;
  private oz = 0;
  private osize = 1;
  private oroll = 0;

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.seedA[i] = hash01(i + 1);
      this.seedB[i] = hash01(i * 3.7 + 11);
      this.seedC[i] = hash01(i * 7.3 + 29);
    }
  }

  setEco(eco: boolean): void {
    if (eco === this.eco) return;
    this.eco = eco;
    // la repartition change : reconfiguration au prochain update
    this.key = '';
  }

  /**
   * Une frame. `style` = aura du chef (null = aucune) ; (x, z) = position monde Three interpolee du chef ; `time` = temps d'animation (s) ;
   * `camQ` = orientation de la camera (billboards).
   */
  update(style: AuraStyle | null, x: number, z: number, time: number, dt: number, camQ: Quaternion): void {
    if (style) {
      if (style !== this.style || this.key === '') {
        const key = this.keyOf(style);
        if (key !== this.key) this.configure(style, key);
        else this.style = style;
      }
    }
    const target = style ? 1 : 0;
    const step = Math.min(dt, 0.1) / FADE_SECONDS;
    this.fade = target > this.fade ? Math.min(1, this.fade + step) : Math.max(0, this.fade - step);

    const st = this.style;
    if (!st || (!style && this.fade <= 0.002)) {
      this.hideAll();
      if (!style) {
        this.style = null;
        this.key = '';
        this.fade = 0;
      }
      return;
    }
    const f = this.fade * this.fade * (3 - 2 * this.fade);
    this.updateColors(st, time);
    this.flash = st.extra === 'lightning' ? this.updateLightning(x, z, time, f, camQ) : 0;
    if (st.extra !== 'lightning') this.ribbons?.hide();
    this.updateParticles(st, x, z, time, f, camQ);
    this.updateGround(st, x, z, time, f);
    this.updateHaloAndPillar(st, x, z, time, f, camQ);
  }

  dispose(): void {
    for (const l of this.layers.values()) {
      this.scene.remove(l.mesh);
      l.mesh.dispose();
    }
    this.layers.clear();
    for (const mesh of [this.halo, this.disc, this.ring, this.dashes, this.ripple, this.pillar]) if (mesh) this.scene.remove(mesh);
    this.halo = this.disc = this.ring = this.dashes = this.ripple = this.pillar = null;
    this.ribbons?.dispose();
    this.ribbons = null;
    this.style = null;
    this.key = '';
    this.ready = false;
  }

  // ------------------------------------------------------------------------------------------------------------------

  private keyOf(s: AuraStyle): string {
    return `${s.form}|${s.particle}|${s.density}|${s.colors[0]}|${s.colors[1]}|${s.rainbow ? 1 : 0}|${s.groundRing ? 1 : 0}|${s.pillar ? 1 : 0}|${s.extra ?? ''}|${this.eco ? 1 : 0}`;
  }

  /** (Re)calcule la repartition des emplacements pour un style / mode eco et masque les couches inutilisees. */
  private configure(style: AuraStyle, key: string): void {
    this.ensureResources();
    const wasNone = this.style === null;
    this.style = style;
    this.key = key;
    const cap = this.eco ? MAX_PARTICLES / 2 : MAX_PARTICLES;
    this.count = Math.min(cap, Math.floor((COUNT_BY_DENSITY[style.density - 1] ?? 8) * (this.eco ? 0.5 : 1)));
    this.sparkEvery = (!this.eco && style.density >= SPARKLE_MIN_DENSITY) || style.extra === 'inferno' ? 3 : 0;
    this.sparkShape = style.extra === 'inferno' ? 'dot' : 'sparkle';
    // Foudre : peu de sprites (les eclairs en rubans font l'essentiel) ; Brasier : flammes en anneau + colonne + braises
    if (style.extra === 'lightning') this.count = Math.min(this.count, this.eco ? 14 : 26);
    // les sprites de cette aura sont crees a la demande
    this.layer(SHAPE_OF[style.particle]);
    if (this.sparkEvery > 0) this.layer(this.sparkShape);
    if (style.extra === 'lightning') this.ribbons ??= new BoltRibbons(this.scene, this.kit, 14);
    if (wasNone) this.fade = 0;
    for (const [shape, l] of this.layers) {
      const used = shape === SHAPE_OF[style.particle] || (this.sparkEvery > 0 && shape === this.sparkShape);
      if (!used) {
        l.mesh.visible = false;
        l.used = 0;
      }
    }
    this.colA.copy(colorOf(style.colors[0]));
    this.colB.copy(colorOf(style.colors[1]));
  }

  /** Cree (une fois) textures, materiaux et maillages du halo / sol / pilier ; tout est suivi par le ResourceTracker (portee globale). */
  private ensureResources(): void {
    if (this.ready) return;
    this.ready = true;
    const kit = this.kit;
    this.spriteMat = kit.track(new MeshBasicMaterial({ color: 0xffffff, vertexColors: true }));

    const glowTex = kit.track(makeGlowTexture());
    const pillarTex = kit.track(makePillarTexture());
    const glowMaterial = (map: CanvasTexture | null, double = false): MeshBasicMaterial =>
      kit.track(
        new MeshBasicMaterial({
          map,
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -2,
          side: double ? DoubleSide : FrontSide,
        }),
      );

    const quad = this.geo.get('aura:quad', () => new PlaneGeometry(1, 1));
    const flatDisc = this.geo.get('aura:flatQuad', () => {
      const g = new PlaneGeometry(2, 2);
      g.rotateX(-Math.PI / 2);
      return g;
    });
    const flatRing = this.geo.get('aura:ring', () => flat(new RingGeometry(0.92, 1, 64, 1)));
    const flatRipple = this.geo.get('aura:ripple', () => flat(new RingGeometry(0.95, 1, 64, 1)));
    const dashGeo = this.geo.get('aura:dashes', () => buildDashes());
    const pillarGeo = this.geo.get('aura:pillar', () => {
      const g = new CylinderGeometry(0.8, 1.1, PILLAR_HEIGHT, 28, 1, true);
      g.translate(0, PILLAR_HEIGHT / 2, 0);
      return g;
    });

    this.halo = this.makeMesh(quad, glowMaterial(glowTex), 3);
    this.disc = this.makeMesh(flatDisc, glowMaterial(glowTex), 1);
    this.ring = this.makeMesh(flatRing, glowMaterial(null), 1);
    this.dashes = this.makeMesh(dashGeo, glowMaterial(null), 1);
    this.ripple = this.makeMesh(flatRipple, glowMaterial(null), 1);
    this.pillar = this.makeMesh(pillarGeo, glowMaterial(pillarTex, true), 2);
    for (const mesh of [this.disc, this.ring, this.dashes, this.ripple]) mesh.position.y = GROUND_Y;
  }

  private makeMesh(geometry: BufferGeometry, material: MeshBasicMaterial, order: number): Mesh {
    const mesh = new Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.renderOrder = order;
    this.scene.add(mesh);
    return mesh;
  }

  private layer(shape: SpriteShape): SpriteLayer {
    let l = this.layers.get(shape);
    if (l) return l;
    const mat = this.spriteMat as MeshBasicMaterial;
    const geometry = this.geo.get(`fx:sprite:${shape}`, () => buildSprite(shape));
    const mesh = new InstancedMesh(geometry, mat, MAX_PARTICLES);
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    const colors = new InstancedBufferAttribute(new Float32Array(MAX_PARTICLES * 3).fill(1), 3);
    colors.setUsage(DynamicDrawUsage);
    mesh.instanceColor = colors;
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.visible = false;
    mesh.renderOrder = 2;
    this.scene.add(mesh);
    l = { mesh, colors, used: 0 };
    this.layers.set(shape, l);
    return l;
  }

  private hideAll(): void {
    for (const l of this.layers.values()) {
      l.mesh.visible = false;
      l.used = 0;
    }
    for (const mesh of [this.halo, this.disc, this.ring, this.dashes, this.ripple, this.pillar]) if (mesh) mesh.visible = false;
    this.ribbons?.hide();
  }

  /** Couleurs courantes (arc-en-ciel : teinte qui tourne) dans colA / colB. */
  private updateColors(st: AuraStyle, time: number): void {
    if (st.rainbow) {
      // teintes un peu plus saturees que les particules : sol et pilier restent lisibles sur un decor clair
      rainbowInto(this.colA, time * 0.2, 0.55);
      rainbowInto(this.colB, time * 0.2 + 0.5, 0.58);
    }
  }

  // ---- particules ------------------------------------------------------------------------------------------------

  private updateParticles(st: AuraStyle, cx: number, cz: number, time: number, fade: number, camQ: Quaternion): void {
    const main = this.layer(SHAPE_OF[st.particle]);
    const spark = this.sparkEvery > 0 ? this.layer(this.sparkShape) : null;
    main.used = 0;
    if (spark) spark.used = 0;
    const base = SIZE_OF[st.particle] * (0.85 + 0.06 * st.density);
    const n = this.count;
    for (let i = 0; i < n; i++) {
      this.place(st, i, n, time);
      const isSpark = spark !== null && i % this.sparkEvery === this.sparkEvery - 1;
      const layer = isSpark && spark ? spark : main;
      if (st.rainbow) {
        rainbowInto(this.colTmp, time * 0.2 + i / n + (i % 2) * 0.08);
      } else {
        this.colTmp.copy(i % 2 === 0 ? this.colA : this.colB);
      }
      const embers = isSpark && st.extra === 'inferno';
      if (embers) this.colTmp.copy(i % 2 === 0 ? this.colB : this.colA).lerp(this.colB, 0.4);
      else if (isSpark) this.colTmp.lerp(WHITE, 0.35);
      const idx = layer.used++;
      this.colTmp.toArray(layer.colors.array, idx * 3);
      const size = (embers ? base * 0.45 : isSpark ? base * 0.95 : base) * this.osize * fade * (0.8 + 0.4 * (this.seedC[i] ?? 0.5));
      const roll = embers ? 0 : isSpark ? time * 3 + i : this.oroll;
      this.p.set(cx + this.ox, this.oy, cz + this.oz);
      this.qRoll.setFromAxisAngle(this.axisZ, roll);
      this.q.copy(camQ).multiply(this.qRoll);
      this.s.set(size, size, size);
      this.m.compose(this.p, this.q, this.s);
      layer.mesh.setMatrixAt(idx, this.m);
    }
    for (const l of this.layers.values()) {
      const active = l === main || l === spark;
      l.mesh.visible = active && l.used > 0;
      if (!active) {
        l.used = 0;
        continue;
      }
      l.mesh.count = l.used;
      l.mesh.instanceMatrix.needsUpdate = true;
      l.colors.needsUpdate = true;
    }
  }

  /** Position locale (par rapport au pied du chat), echelle relative et rotation d'une particule : ox, oy, oz, osize, oroll. */
  private place(st: AuraStyle, i: number, n: number, t: number): void {
    const sa = this.seedA[i] ?? 0;
    const sb = this.seedB[i] ?? 0;
    const sc = this.seedC[i] ?? 0;
    const u = i / n;
    let size = 1;
    if (st.extra === 'inferno' && i % 3 !== 0) {
      if (i % 3 === 1) {
        // colonne de flammes qui monte du corps (plus haute que Flammes)
        const ph = frac(t * 0.75 + u * 5.3 + sa);
        const ang = sb * TAU + t * 1.3;
        const r = (0.18 + 0.32 * sc) * (1 - ph * 0.45);
        this.ox = Math.cos(ang) * r;
        this.oz = Math.sin(ang) * r;
        this.oy = 0.1 + ph * 3.5;
        this.osize = Math.pow(Math.sin(Math.PI * ph), 0.5) * 1.35;
        this.oroll = Math.sin(t * 7 + i) * 0.14;
      } else {
        // braises : montent plus haut en derivant et en rapetissant
        const ph = frac(t * 0.42 + sa * 3);
        const ang = sb * TAU + t * 0.5 + ph * 2;
        const r = 0.3 + 0.9 * sc * ph;
        this.ox = Math.cos(ang) * r;
        this.oz = Math.sin(ang) * r;
        this.oy = 0.25 + ph * 4.3;
        this.osize = Math.pow(1 - ph, 0.6) * Math.min(1, ph * 12) * 1.2;
        this.oroll = 0;
      }
      return;
    }
    switch (st.form) {
      case 'glow': {
        // lucioles qui flottent autour du corps
        const a = u * TAU + t * 0.3;
        const r = 0.8 + 0.5 * sb + 0.12 * Math.sin(t * 0.9 + sa * TAU);
        this.ox = Math.cos(a) * r;
        this.oz = Math.sin(a) * r * 0.85;
        this.oy = 0.3 + 1.4 * (0.5 + 0.5 * Math.sin(t * (0.35 + 0.25 * sa) + sb * TAU));
        size = 0.75 + 0.35 * Math.sin(t * 2.6 + sa * TAU * 2);
        break;
      }
      case 'ring': {
        // anneau(x) de particules qui tournent autour des pieds
        const second = st.density >= 4 && i % 2 === 1;
        const dir = second ? -1 : 1;
        const a = u * TAU + dir * t * (1.1 + 0.25 * st.density);
        const r = (second ? 1.05 : 1.5) + 0.07 * Math.sin(t * 3 + i);
        this.ox = Math.cos(a) * r;
        this.oz = Math.sin(a) * r;
        this.oy = 0.28 + (second ? 0.55 : 0) + 0.12 * Math.sin(t * 4 + i * 1.9);
        size = 1 + 0.15 * Math.sin(t * 7 + i * 2.1);
        break;
      }
      case 'orbit': {
        // trois orbites inclinees autour du corps
        const k = i % 3;
        const tilt = k === 0 ? 0.12 : k === 1 ? 0.95 : -0.95;
        const yaw = (k * TAU) / 3 + 0.5;
        const w = (0.9 + 0.3 * k) * (k === 2 ? -1 : 1);
        const a = (Math.floor(i / 3) / Math.max(1, n / 3)) * TAU + w * t;
        const r = 1.1 + 0.18 * k;
        const lx = Math.cos(a) * r;
        const lz = Math.sin(a) * r;
        const ty = lz * Math.sin(tilt);
        const tz = lz * Math.cos(tilt);
        this.ox = lx * Math.cos(yaw) + tz * Math.sin(yaw);
        this.oz = -lx * Math.sin(yaw) + tz * Math.cos(yaw);
        this.oy = BODY_Y + ty;
        size = 0.9 + 0.2 * Math.sin(t * 3 + sa * TAU);
        break;
      }
      case 'rise': {
        // montee depuis les pieds, en s'ecartant et en s'estompant
        const rate = 0.32 + 0.07 * st.density;
        const ph = frac(sa + t * rate * (0.8 + 0.4 * sb));
        const ang = sb * TAU + t * 0.9 + ph * 2.5;
        const r = (0.45 + 0.5 * sc) * (0.8 + 0.5 * ph);
        this.ox = Math.cos(ang) * r;
        this.oz = Math.sin(ang) * r;
        this.oy = 0.05 + ph * 2.7;
        size = Math.pow(Math.sin(Math.PI * ph), 0.6);
        break;
      }
      case 'spiral': {
        // helice ascendante a deux brins
        const ph = frac(t * 0.3 + u);
        const ang = ph * TAU * 2.2 + (i % 2) * Math.PI + t * 0.6;
        const r = 1.3 - 0.3 * ph + 0.1 * Math.sin(ph * TAU * 3);
        this.ox = Math.cos(ang) * r;
        this.oz = Math.sin(ang) * r;
        this.oy = 0.05 + ph * 2.7;
        size = Math.pow(Math.sin(Math.PI * ph), 0.5);
        break;
      }
      case 'burst': {
        // emissions radiales courtes, direction differente a chaque cycle
        const cyc = t / BURST_PERIOD + sa;
        const ph = frac(cyc);
        const ang = hash01(i * 17.3 + Math.floor(cyc) * 3.1) * TAU;
        const r = 0.45 + ph * (1.9 + 1.3 * sb);
        this.ox = Math.cos(ang) * r;
        this.oz = Math.sin(ang) * r;
        this.oy = BODY_Y + (sc - 0.5) * 0.9 + Math.sin(ph * Math.PI) * 0.5 - ph * ph * 0.35;
        size = Math.pow(1 - ph, 0.7) * Math.min(1, ph * 9) * 1.15;
        this.oroll = hash01(i * 5.1 + Math.floor(cyc) * 9.7) * TAU;
        this.osize = size;
        return;
      }
    }
    this.osize = size;
    this.oroll = this.rollOf(st.particle, i, t, sa);
  }

  /** Rotation dans le plan de l'ecran selon le type de particule (flammes droites, etoiles et flocons qui tournent...). */
  private rollOf(kind: AuraParticle, i: number, t: number, sa: number): number {
    switch (kind) {
      case 'star':
        return t * (sa - 0.5) * 4 + sa * 6;
      case 'heart':
        return Math.sin(t * 3 + sa * 6) * 0.25;
      case 'flame':
        return Math.sin(t * 6 + i) * 0.12;
      case 'snow':
        return t * (sa - 0.5) * 3 + sa * 6;
      case 'petal':
        return t * (sa - 0.5) * 4 + sa * 6;
      case 'note':
        return Math.sin(t * 2.5 + sa * 6) * 0.3;
      case 'bolt':
        return (sa - 0.5) * 0.5;
      default:
        return 0;
    }
  }

  /**
   * Foudre : arcs electriques qui crepitent autour du corps (changent ~14 fois par seconde, certains manquent) et eclairs qui frappent le sol
   * autour du chat (courts, a periode decalee). Retourne l'intensite 0..1 du flash de halo (jamais de flash plein ecran).
   */
  private updateLightning(cx: number, cz: number, time: number, fade: number, camQ: Quaternion): number {
    const rb = this.ribbons;
    if (!rb) return 0;
    this.camRight.set(1, 0, 0).applyQuaternion(camQ);
    this.camUp.set(0, 1, 0).applyQuaternion(camQ);
    rb.begin();
    const step = Math.floor(time * 14);
    const arcs = this.eco ? 3 : 5;
    for (let k = 0; k < arcs; k++) {
      const seed = step * 3.1 + k * 17.3;
      if (hash01(seed * 1.3) < 0.22) continue;
      const a = hash01(k * 9.1) * TAU + time * 0.8 * (k % 2 === 0 ? 1 : -1);
      const a2 = a + (0.6 + 0.9 * hash01(seed)) * (hash01(seed + 2) < 0.5 ? -1 : 1);
      const r2 = 1.2 + 0.7 * hash01(seed + 5);
      rb.bolt(cx + Math.cos(a) * 0.5, 0.5 + 0.9 * hash01(k * 3.3 + step * 0.37), cz + Math.sin(a) * 0.5, cx + Math.cos(a2) * r2, 0.2 + 1.9 * hash01(seed + 7), cz + Math.sin(a2) * r2, seed, 0.22, 7, 0.22, this.camRight, this.camUp);
    }
    let flash = 0;
    const strikes = this.eco ? 2 : 3;
    for (let k = 0; k < strikes; k++) {
      const period = 1.15 + 0.35 * k;
      const cyc = time / period + k / 3;
      const n = Math.floor(cyc);
      const ph = (cyc - n) * period;
      if (ph >= STRIKE_SECONDS) continue;
      const a = hash01(n * 7.7 + k * 3) * TAU;
      const r = 1.3 + 1.7 * hash01(n * 3.1 + k);
      const x = cx + Math.cos(a) * r;
      const z = cz + Math.sin(a) * r;
      rb.bolt(x + (hash01(n * 1.9 + k) - 0.5) * 1.2, 5.4, z, x, 0.05, z, n * 5.5 + k, 0.5, 10, 0.36, this.camRight, this.camUp);
      flash = Math.max(flash, 1 - ph / STRIKE_SECONDS);
    }
    rb.end(this.colA, fade);
    return flash;
  }

  /** Teinte du halo / pilier : melange principale -> secondaire (arc-en-ciel : la teinte principale seule, sinon le melange tourne au gris). */
  private tint(st: AuraStyle, k: number): Color {
    return this.colTmp.copy(this.colA).lerp(this.colB, st.rainbow ? 0 : k);
  }

  // ---- sol, halo, pilier -----------------------------------------------------------------------------------------

  private updateGround(st: AuraStyle, cx: number, cz: number, time: number, fade: number): void {
    const on = st.groundRing === true;
    for (const mesh of [this.disc, this.ring, this.dashes, this.ripple]) if (mesh) mesh.visible = on;
    if (!on || !this.disc || !this.ring || !this.dashes || !this.ripple) return;
    const dens = st.density;
    const pulse = 0.5 + 0.5 * Math.sin(time * 2.2);
    const c0 = this.colA;
    const c1 = this.colB;

    const rd = 1.7 + 0.18 * dens;
    this.disc.position.x = cx;
    this.disc.position.z = cz;
    this.disc.scale.set(rd, 1, rd);
    setMaterial(this.disc, c1, (0.3 + 0.12 * pulse) * fade);

    const rr = 1.45 + 0.04 * Math.sin(time * 3);
    this.ring.position.x = cx;
    this.ring.position.z = cz;
    this.ring.scale.set(rr, 1, rr);
    setMaterial(this.ring, c0, 0.85 * fade);

    this.dashes.position.x = cx;
    this.dashes.position.z = cz;
    this.dashes.rotation.y = -time * 0.6;
    const rdash = 1.85 + 0.05 * Math.sin(time * 2);
    this.dashes.scale.set(rdash, 1, rdash);
    setMaterial(this.dashes, c1, 0.7 * fade);

    // onde qui s'etend et s'efface (inactive en eco)
    const k = frac(time / RIPPLE_SECONDS);
    const rp = 0.6 + 1.9 * k;
    this.ripple.visible = !this.eco;
    this.ripple.position.x = cx;
    this.ripple.position.z = cz;
    this.ripple.scale.set(rp, 1, rp);
    setMaterial(this.ripple, c0, (1 - k) * (1 - k) * 0.7 * fade);
  }

  private updateHaloAndPillar(st: AuraStyle, cx: number, cz: number, time: number, fade: number, camQ: Quaternion): void {
    const halo = this.halo;
    if (halo) {
      const isGlow = st.form === 'glow';
      const inferno = st.extra === 'inferno';
      const flash = this.flash;
      const strength = (isGlow ? 0.55 : inferno ? 0.5 : st.density >= 4 ? 0.3 : 0) + flash * 0.5;
      halo.visible = strength > 0;
      if (strength > 0) {
        // Brasier : halo orange qui respire plus vite (chaleur, sans distorsion) ; Foudre : halo qui claque en blanc-bleu a chaque impact
        const size = ((isGlow ? 3.3 : inferno ? 4.2 : 3.0 + 0.15 * st.density) + flash * 0.9) * (1 + 0.07 * Math.sin(time * (inferno ? 5 : 1.7)));
        halo.position.set(cx, 1.15, cz - 0.45);
        halo.quaternion.copy(camQ);
        halo.scale.set(size, size, 1);
        const wobble = inferno ? 0.75 + 0.25 * Math.sin(time * 6.3) * Math.sin(time * 2.1) : 0.8 + 0.2 * Math.sin(time * 2.1);
        setMaterial(halo, this.tint(st, isGlow ? 0.25 : inferno ? 0.5 : 0.4).lerp(WHITE, flash * 0.8), strength * wobble * fade);
      }
    }
    const pillar = this.pillar;
    if (pillar) {
      pillar.visible = st.pillar === true;
      if (st.pillar) {
        const pulse = 1 + 0.05 * Math.sin(time * 2.4);
        pillar.position.set(cx, 0, cz);
        pillar.rotation.y = time * 0.7;
        pillar.scale.set(pulse, 1, pulse);
        setMaterial(pillar, this.tint(st, 0.35), (0.46 + 0.07 * Math.sin(time * 3.1)) * fade);
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------------

const WHITE = new Color(1, 1, 1);
const GROUND_Y = 0.05;
const PILLAR_HEIGHT = 6.5;
const BURST_PERIOD = 1.15;
const RIPPLE_SECONDS = 1.8;
/** Duree d'un eclair qui frappe le sol (s). */
const STRIKE_SECONDS = 0.22;

const frac = (v: number): number => v - Math.floor(v);

const setMaterial = (mesh: Mesh, color: Color, opacity: number): void => {
  const m = mesh.material as MeshBasicMaterial;
  m.color.copy(color);
  m.opacity = opacity;
};

/** Geometrie posee a plat (normale +Y) : anneaux au sol. */
const flat = (g: BufferGeometry): BufferGeometry => {
  g.rotateX(-Math.PI / 2);
  return g;
};

/** Anneau en tirets (12 arcs) pose a plat. */
const buildDashes = (): BufferGeometry => {
  const parts: BufferGeometry[] = [];
  const n = 12;
  for (let i = 0; i < n; i++) parts.push(flat(new RingGeometry(1, 1.1, 6, 1, (i / n) * TAU, (TAU / n) * 0.55)));
  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  return merged ?? flat(new RingGeometry(1, 1.1, 32, 1));
};

/** Degrade radial blanc -> transparent (halo, disque au sol). */
const makeGlowTexture = (): CanvasTexture => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    grad.addColorStop(0.7, 'rgba(255,255,255,0.12)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }
  return new CanvasTexture(canvas);
};

/** Pilier : opacite croissante vers le bas, 3 rayons doux autour du cylindre (le maillage tourne : les rayons balaient). */
const makePillarTexture = (): CanvasTexture => {
  const w = 96;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const img = ctx.createImageData(w, h);
    for (let y = 0; y < h; y++) {
      const v = y / (h - 1);
      const fadeY = Math.pow(v, 0.9);
      for (let x = 0; x < w; x++) {
        const ray = 0.55 + 0.45 * Math.cos((x / w) * TAU * 3);
        const i = (y * w + x) * 4;
        img.data[i] = 255;
        img.data[i + 1] = 255;
        img.data[i + 2] = 255;
        img.data[i + 3] = Math.round(255 * fadeY * ray);
      }
    }
    ctx.putImageData(img, 0, 0);
  }
  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  return tex;
};
